<template>
  <header class="header-container">
    <b-sidebar id="sidebar-right" right shadow backdrop @change="sidebarToggle">
      <template slot="title">
        <font-awesome-icon :icon="['fal', 'user-cog']"></font-awesome-icon> {{ $t('sidenav.settings') }}
      </template>
      <div class="px-3 pt-2 sidebar-container">
        <div class="col-xs-3 offset-xs-3 profile-picture-container" @click="openModal('profile-modal')">
          <img :src="currentUser.data.links.profile_picture_url" alt="Profile Picture" class="profile-icon d-block mx-auto" />
        </div>
        <h3 class="card-title text-center py-2 my-2 full-name">{{ fullName }}</h3>

        <div class="grid-container" @click="openAdministrator">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'user']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.administrator') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <span class="text-muted heading py-1">{{ $t('sidenav.communication') }}</span>
        <div class="grid-container" @click="openEmailSettings">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'bell']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.emailSettings') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <span class="text-muted heading py-1">{{ $t('sidenav.legal') }}</span>
        <div class="grid-container" @click="openModal('tos-modal')">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'file']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.termsOfService') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <hr class="mt-1 mb-1" />
        <div class="grid-container" @click="openModal('pp-modal')">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'file']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.privacyPolicy') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <span class="text-muted heading py-1">{{ $t('sidenav.system') }}</span>
        <div class="grid-container" @click="openHelp">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'question-circle']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.help') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <hr class="mt-1 mb-1" />
        <div class="grid-container" @click="openResetPassword">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'lock']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.resetPassword') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <hr class="mt-1 mb-1" v-if="multiOrgs" />
        <div class="grid-container" @click="navigateOrganizationSelector" v-if="multiOrgs">
          <div class="icon">
            <font-awesome-icon :icon="['fal', 'user-friends']"></font-awesome-icon>
          </div>
          <div class="title">{{ $t('sidenav.selectOrganization') }}</div>
          <div class="link">
            <font-awesome-icon :icon="['fal', 'chevron-right']" class="text-secondary"></font-awesome-icon>
          </div>
        </div>
        <hr class="mt-1 mb-3" v-if="multiOrgs" />
        <b-button class="w-100 sign-out-btn" @click="signOut">{{ $t('sidenav.signOut') }}</b-button>
        <span class="version-text text-muted mx-auto d-block text-center mt-2">{{ $t('sidenav.version', { version: version }) }}</span>
      </div>
    </b-sidebar>
    <nav class="nav-container">
      <p v-if="defaultOrganizationName()">
        {{ defaultOrganizationName() }}
      </p>
      <button class="user-button float-right p-1 m-1" v-b-toggle.sidebar-right @click="sidebarToggle()">
        <font-awesome-icon :icon="['fal', 'user-cog']" class="user-icon-button"></font-awesome-icon>
      </button>
    </nav>
    <help-modal id="help-modal"></help-modal>
    <reset-password-modal id="reset-password-modal" :closeVisible="true" :redirectHome="false"></reset-password-modal>
    <!-- Privacy Policy Modal -->
    <base-modal id="pp-modal">
      <div class="legal-container" v-html="privacyPolicy"></div>
    </base-modal>
    <base-modal id="tos-modal">
      <div class="legal-container" v-html="termsOfService"></div>
    </base-modal>
    <!-- Profile Modal -->
    <base-modal id="profile-modal">
      <input ref="avatar" type="file" id="avatar" name="avatar" accept="image/png, image/jpeg" hidden @change="uploadAvatar">
      <h5 class="text-center">{{ $t('sidenav.profileCard.profile') }}</h5>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.avatarImage') }}</span>
      <img :src="currentUser.data.links.profile_picture_url" alt="Profile Picture" class="profile-icon d-block mx-auto p-1" />
      <p class="text-center my-2 full-name"><strong>{{ fullName }}</strong></p>
      <button class="d-block btn mx-auto mb-2 update-profile-btn" @click="openFileBrowser">{{ $t('sidenav.profileCard.updateProfileButton') }}</button>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.email') }}</span>
      <div class="p-2 modal-text">
        <font-awesome-icon :icon="['fal', 'envelope']" class="text-secondary"></font-awesome-icon>
        {{ currentUser.data.attributes.email }}
      </div>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.location') }}</span>
      <div class="p-2 modal-text">
        <font-awesome-icon :icon="['fal', 'location']" class="text-secondary"></font-awesome-icon>
        {{ currentUser.data.attributes.city }}, {{ currentUser.data.attributes.state }}
      </div>
    </base-modal>
    <!-- Administrator Modal -->
    <base-modal id="admin-modal" v-if="coachMember && coachUser">
      <h5 class="text-center">{{ $t('sidenav.adminCard.profile') }}</h5>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.avatarImage') }}</span>
      <img :src="coachUser.links.profile_picture_url" alt="Profile Picture" class="profile-icon d-block mx-auto p-1" />
      <p class="text-center my-2"><strong>{{ fullCoachName }}</strong></p>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.email') }}</span>
      <div class="p-2 modal-text">
        <font-awesome-icon :icon="['fal', 'envelope']" class="text-secondary"></font-awesome-icon>
        {{ coachUser.attributes.email }}
      </div>
      <span class="text-muted heading">{{ $t('sidenav.profileCard.location') }}</span>
      <div class="p-2 modal-text">
        <font-awesome-icon :icon="['fal', 'location']" class="text-secondary"></font-awesome-icon>
        {{ coachUser.attributes.city }}, {{ coachUser.attributes.state }}
      </div>
    </base-modal>
    <!-- Confirmation Modal -->
    <confirmation-modal
      id="confirm-modal"
      :title="$t('communicationModal.title')"
      :description="$t('communicationModal.description')"
      @cancel="emailNotificationToggle = !emailNotificationToggle"
      @confirm="updateEmailNotification(false)">
    </confirmation-modal>
    <email-settings id="email-settings-modal" :member="currentMember" v-if="currentMember" />
  </header>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { destroyToken, parseToken } from '@/services/jwt.service';
import {
  updateApi,
  createApi,
  queryApi,
} from '@/services/api.service';
import UserService from '@/services/user.service';
import MemberService from '@/services/member.service';
import CommonFunctions from '@/mixins/common';

export default {
  name: 'BaseHeader',
  mixins: [CommonFunctions],
  components: {
    ConfirmationModal: () => import('@/components/confirmation-modal/confirmation-modal.vue'),
    HelpModal: () => import('@/components/help-modal/help-modal.vue'),
    ResetPasswordModal: () => import('@/components/reset-password-modal/reset-password-modal.vue'),
    BaseModal: () => import('@/components/base-modal/base-modal.vue'),
    EmailSettings: () => import('@/components/email-settings/email-settings'),
  },
  data() {
    return {
      coachMember: null,
      coachUser: null,
      token: parseToken(),
      multiOrgs: false,
      version: process.env.VUE_APP_VERSION,
    };
  },
  created() {
    this.getCoach();
    this.getOrganizationMemberships();
  },
  computed: {
    ...mapState(['currentUser', 'currentMember', 'defaultOrganization', 'sessionUuid']),
    ...mapGetters(['termsOfService', 'privacyPolicy']),
    avatar: {
      get() { return this.currentUser.data.links.profile_picture_url; },
      set(newValue) {
        const currentUser = { ...this.currentUser };
        currentUser.data.links.profile_picture_url = newValue;
        this.setCurrentUser(currentUser);
      },
    },
    emailNotificationToggle: {
      get() {
        return this.currentMember.attributes.enable_email_notifications;
      },
      set(newValue) {
        const currentMember = { ...this.currentMember };
        currentMember.attributes.enable_email_notifications = newValue;
        this.setCurrentMember(currentMember);
        if (newValue === false) {
          this.openModal('confirm-modal');
        } else {
          this.updateEmailNotification(newValue);
        }
      },
    },
    fullName() {
      if (this.currentUser) {
        return `${this.currentUser.data.attributes.first_name} ${this.currentUser.data.attributes.last_name}`;
      }
      return '';
    },
    fullCoachName() {
      if (this.coachUser) {
        return `${this.coachUser.attributes.first_name} ${this.coachUser.attributes.last_name}`;
      }
      return '';
    },
  },
  methods: {
    ...mapMutations(['setCurrentUser', 'setCurrentMember', 'setDefaultOrganization', 'setSessionUuid', 'clearAllGlobals']),
    getCurrentUser() {
      return queryApi(`users/${this.token.sub}`);
    },
    sidebarToggle(visible) {
      const appContainer = document.getElementsByClassName('app-container');
      const isOpen = visible;

      if (isOpen && appContainer.length > 0) {
        appContainer[0].style.overflow = 'hidden';
      } else if (!isOpen && appContainer.length > 0) {
        appContainer[0].style.overflow = 'auto';
      }
    },
    getCoach() {
      const params = {
        per_page: 1,
        'query[role_eq]': 'Administrator',
        'query[userable_type_eq]': 'User',
        'query[memberable_type_eq]': 'V2::Organization',
        'query[memberable_id_eq]': this.defaultOrganization.data.id,
        'query[sorts]': 'created_at ASC',
      };
      return MemberService.index(params)
        .then(({ data }) => {
          this.log('BaseHeader -> GetCoach -> MemberService.index', 'data', data);
          this.coachMember = data;
          if (this.coachMember.length > 0) {
            const userId = this.coachMember[0].relationships.user.data.id;
            return UserService.show(userId);
          }
          throw new Error('no coach found!');
        })
        .then(({ data }) => {
          this.coachUser = data;
        })
        .catch(console.error);
    },
    signOut() {
      if (this.currentUser && this.currentUser.data) {
        UserService.signOutSignal(this.currentUser.data.id, this.sessionUuid);
      }
      setTimeout(() => {
        destroyToken();
        // if route was successful, remove the vuex main variables.
        this.$router.push({ name: 'Login' }, () => {
          if (process.env.VUE_APP_ENV !== 'development') {
            this.$honeybadger.resetContext();
          }
          this.clearAllGlobals();
          localStorage.clear();
        });
      }, 500);
    },
    openHelp() {
      this.openModal('help-modal');
    },
    openResetPassword() {
      this.openModal('reset-password-modal');
    },
    openAdministrator() {
      this.openModal('admin-modal');
    },
    openFileBrowser() {
      this.$refs.avatar.click();
    },
    openEmailSettings() {
      this.openModal('email-settings-modal');
    },
    uploadAvatar(e) {
      const { target } = e;
      const params = new FormData();
      params.append('file', target.files[0]);
      createApi(`users/${this.token.sub}/avatar_upload`, params)
        .then(({ data }) => {
          this.avatar = data.data.links.profile_picture_url;
        });
    },
    updateEmailNotification(emailNotificationToggle) {
      const params = {
        data: {
          attributes: {
            enable_email_notifications: emailNotificationToggle,
          },
        },
      };
      updateApi(`members/${this.currentMember.id}`, params).then(({ data }) => {
        console.info('User set email notifications for membership to', data.data.attributes.enable_email_notifications);
      });
    },
    navigateOrganizationSelector() {
      this.safelyNavigateToPath('OrganizationSelector');
    },
    getOrganizationMemberships() {
      const memberParams = {
        'query[memberable_type_eq]': 'V2::Organization',
        'query[userable_id_eq]': this.token.sub,
        'query[userable_type_eq]': 'User',
        'query[sorts]': 'default DESC',
        'query[archived_eq]': '0',
        per_page: 10,
      };
      return queryApi('members', memberParams)
        .then(({ data }) => {
          if (data.data.length > 1) {
            this.multiOrgs = true;
          }
        });
    },
    defaultOrganizationName() {
      return this.getOrganizationName(this.defaultOrganization.data);
    },
  },
};
</script>

<style scoped lang="scss" src="./styles.scss">

</style>
